<template>
  <transition :name="isAuthorEditMode ? '' : 'flyout-slide'" @after-enter="handleFlyoutIsOpen" appear>
    <div
      :class="[
        'owc-header-flyout',
        isFirstLevelFlyout ? 'owc-header-flyout__first-level' : '',
        isLastLevelDesktop ? 'owc-header-flyout__last-level' : '',
      ]"
      @keydown="keyDownHandler"
      role="menu"
    >
      <div class="owc-header-flyout__topbar" ref="flyoutTopbar">
        <div v-if="isFirstLevelFlyout" class="owc-header-flyout__topbar-container">
          <div class="owc-header-flyout__topbar-content">
            <h3
              :class="[
                'owc-header-flyout__topbar-desktop-title',
                shouldShowDesktopTopTitle ? 'owc-header-flyout__topbar-desktop-title--show' : '',
              ]"
            >
              {{ flyoutTitle }}
            </h3>
            <div class="owc-header-flyout__topbar-favourite-and-fss">
              <!-- To be replaced with slot container with the new headerbutton components  -->
              <!-- To be replaced with slot container with the new headerbutton components  -->
              <!-- <OwcHeaderButton
              class="owc-header-flyout__topbar-mobileElement"
              icon="favorite"
              aria-label="Favorite"
            /> -->
            </div>
            <OwcLogo class="owc-header-flyout__topbar-mobileElement owc-header-flyout__logo" />
            <OwcHeaderButton
              class="owc-header-flyout__topbar-closeButton"
              :aria-label="closeButtonLabel ? closeButtonLabel : ''"
              @click.native="handleFlyoutCloseClick(true)"
              icon="bds/close/24"
              tabindex="-1"
              :isIconSmall="false"
              :lightMode="true"
            />
          </div>
          <div class="owc-header-flyout__topbar-fss-content">
            <div class="owc-header-flyout__topbar-mobileElement owc-header-flyout__topbar-fssElement">
              <slot name="fss-search-input"></slot>
            </div>
          </div>
        </div>
        <div v-else class="owc-header-flyout__topbar-content">
          <!--TODO:ARIA -->
          <button
            v-if="isFlyoutNavVisible"
            :aria-label="backButtonLabel ? backButtonLabel : ''"
            class="wb-button owc-header-flyout__topbar-backButton"
            @click="handleFlyoutCloseClick(false)"
            tabindex="-1"
          >
            <OwcHeaderButton
              class="owc-header-flyout__topbar-backButtonWrapper"
              aria-hidden="true"
              icon="bds/chevron-left/24"
              :isIconSmall="false"
              :lightMode="true"
            />
            {{ flyoutTitle }}
          </button>
          <h3 v-else class="owc-header-flyout__topbar-models">{{ flyoutTitle }}</h3>
          <!--TODO:ARIA -->
          <OwcHeaderButton
            class="owc-header-flyout__topbar-closeButton"
            :aria-label="closeButtonLabel ? closeButtonLabel : ''"
            @click.native="handleFlyoutCloseClick(true)"
            icon="bds/close/24"
            tabindex="-1"
            :isIconSmall="false"
            :lightMode="true"
          />
        </div>
      </div>
      <div class="owc-header-flyout__innerContent" ref="flyoutInnerContent" @scroll="throttleOpacityChange">
        <div v-if="isFirstLevelFlyout" class="owc-header-flyout__inner-content-title">
          {{ flyoutTitle }}
        </div>

        <SlotContainer
          v-if="isAuthorEditMode"
          class="owc-header-flyout__navigation-list"
          refName="slot-header-flyout"
          elementDOM="ul"
          :slot-component-data="stringifiedSlotData"
        >
          <slot></slot>
        </SlotContainer>
        <div v-else>
          <ul
            :class="[
              'owc-header-flyout__navigation-list',
              { 'owc-header-flyout__navigation-list--first-level': isFirstLevelFlyout },
            ]"
          >
            <!--
              This is here so that VMOS can reuse the owc-header-flyout for both a level 1 and a level 2
              flyout. Unfortunately this will require slots. If we use <slot> the inner slot(level 2)
              is duplicated and the styles won't work anymore because they are being applied to the copy of the slot
              and not the slot itself. 

              For that reason, this horrible thing exists.
            -->

            <SlotContainer
              v-if="shouldShowVmos"
              refName="slot-seamless-vmos-flyout"
              slotName="seamless-vmos-flyout"
            >
              <slot slot="seamless-vmos-flyout" name="seamless-vmos-flyout" />
            </SlotContainer>

            <div
              v-for="(listItem, index) in flyoutInnerItems"
              :key="`${listItem.label}-${index}`"
              :is="getInnerFlyoutElementType(listItem.itemType)"
              :datajson="stringifiedJsonData({ ...listItem, listIndex: index })"
            >
              <slot
                v-if="shouldAddVmosSlot(listItem, index)"
                slot="seamless-vmos-flyout"
                name="seamless-vmos-flyout"
              ></slot>
            </div>
          </ul>
        </div>
        <a v-if="imprint" class="owc-header-flyout__imprint" :target="imprint.target" :href="imprint.url">
          {{ imprint.label }}
        </a>
      </div>
      <div v-if="languageConfigurations" class="owc-header-flyout__bottom-bar">
        <OwcLanguageSwitch :languageSwitch="languageConfigs" />
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import {
  HeaderFlyoutComponent,
  HeaderFlyoutData,
  HeaderFlyoutMethods,
  HeaderFlyoutComputed,
  HeaderFlyoutProps,
} from '@owc-packages/Header/src/HeaderFlyout';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import OwcLanguageSwitch from '@owc-packages/Header/src/LanguageSwitch.vue';
import OwcLogo from '@owc-packages/Header/src/HeaderLogo.vue';
import OwcHeaderButton from '@owc-packages/Header/src/HeaderButton.vue';
import OwcHeaderNavigationToolList from '@owc-packages/Header/src/HeaderNavigationToolList.vue';
import OwcHeaderNavigationLinkList from '@owc-packages/Header/src/HeaderNavigationLinkList.vue';
import OwcHeaderNavigationLink from '@owc-packages/Header/src/HeaderNavigationLink.vue';
import OwcHeaderNavigationTeaser from '@owc-packages/Header/src/HeaderNavigationTeaser.vue';
import headerMixin from '@owc-utils/Mixins/Header';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { desktopBreakpoints } from '@owc-utils/MediaQueryService/MediaQueryUtils';
import { lockWindowScroll, unlockWindowScroll } from '@owc-utils/common/LockWindow';
import { throttle } from '@owc-utils/Events';
import { THROTTLE_TIMER } from './Header.configs';
import { AccessibilityFocusManager } from '@owc-utils/common/AccessibilityFocusHelper';
import getAccessibilityStackInstance from '@owc-utils/common/AccessibilityFocusStackHelper';
import { FlyoutInnerItemsType } from './Header.enums';
import { setupHostApi, dispatchMainFlyoutCloseEvent } from '@owc-utils/Seamless/flyoutIntegrations';
import { updateActiveFlyout } from '@owc-utils/Seamless/headerIntegrations';
import { headerFlyoutFocusHandler } from '@owc-packages/Header/src/HeaderUtils';
/*
 * A flyout is a normal Vue component that can have X topics, entries or links inside of it
 *  Each topic can have inside of it another Flyout.
 *  FlyoutData is used to inform inner flyouts in which level they are open.
 *  this currentFlyoutLevel is calculated based on the parent of the flyout.
 *
 *  If parent == HeaderNavigation => level 1
 *  If parent == Topic => level 2 or 3.
 *
 * The destintion between 2 and 3 is not relevant since they are visually identical.
 */
const globalFocusStack = getAccessibilityStackInstance();
const scrollHeightTriggerPoint = 3;
let flyoutChainSize = 0;

export default (ComponentInitializeDispatcher as HeaderFlyoutComponent).extend<
  HeaderFlyoutData,
  HeaderFlyoutMethods,
  HeaderFlyoutComputed,
  HeaderFlyoutProps
>({
  name: 'owc-header-flyout',
  mixins: [headerMixin],
  components: {
    SlotContainer,
    OwcLogo,
    OwcHeaderButton,
    OwcLanguageSwitch,
    OwcHeaderNavigationLink,
    OwcHeaderNavigationTeaser,
    OwcHeaderNavigationToolList,
    OwcHeaderNavigationLinkList,
    OwcHeaderNavigationTopic: () => import('@owc-packages/Header/src/HeaderNavigationTopic.vue'),
  },
  props: {
    flyoutTitle: {
      required: false,
      type: String,
    },
    languageConfigs: {
      required: false,
      type: Array,
    },
    imprint: {
      required: false,
      type: Object,
    },
    currentFlyoutLevel: {
      required: true,
      type: Number,
    },
    isFlyoutNavVisible: {
      required: true,
      type: Boolean,
    },
    isThisFlyoutVisible: {
      required: false,
      type: Boolean,
    },
    flyoutInnerItems: {
      required: false,
      type: Array,
    },
    /* Since the flyout is not a webcomponent
     * we have to pass this value directly
     **/
    isAuthorEditMode: {
      required: false,
      type: Boolean,
    },
    flyoutId: {
      required: false,
      type: String,
    },
    isSecondaryVmosFlyout: {
      required: false,
      type: Boolean,
    },
  },
  data() {
    return {
      isLastLevelDesktop: false,
      isFirstLevelDesktop: false,
      closeButtonLabel: '',
      backButtonLabel: '',
      accessibilityHelper: new AccessibilityFocusManager(),
      scrollHandler: null,
      shouldShowDesktopTopTitle: false,
    };
  },
  mounted() {
    if (this.onMQChange) {
      this.onMQChange(this.handleResize as EventListenerOrEventListenerObject);
    }
    if (this.getCurrentMediaQuery) {
      this.setFlyoutLevels(this.getCurrentMediaQuery());
    }

    this.storeStateChangeListener = this.onHeaderStateChange(state => {
      this.closeButtonLabel = state.navigationLabels?.closeButton;
      this.backButtonLabel = state.navigationLabels?.backButton;

      flyoutChainSize = state.flyoutChain?.length;
    });

    setupHostApi(this);
  },
  beforeDestroy() {
    if (this.storeStateChangeListener) {
      this.removeOnHeaderStateChange(this.storeStateChangeListener);
    }
  },
  methods: {
    initAccessibilityHelper() {
      const state = this.getHeaderStoreState();
      flyoutChainSize = state.flyoutChain?.length;

      if (!this.accessibilityHelper.isInitialized && this.$refs.flyoutInnerContent) {
        const innerContent = this.$refs.flyoutInnerContent;

        this.accessibilityHelper.init(innerContent, '.owc-header-flyout__navigation-list');
      }
    },
    throttleOpacityChange() {
      if (this.isFirstLevelDesktop) {
        if (!this.scrollHandler) {
          this.scrollHandler = throttle(THROTTLE_TIMER, this.calculateHeadlineOpacity);
        }
        this.scrollHandler();
      }
    },
    handleFlyoutCloseClick(isClose) {
      this.$el.classList.add('owc-header-flyout--allow-animation');

      if (this.isSecondaryVmosFlyout) {
        dispatchMainFlyoutCloseEvent(this, {
          isClose: isClose,
          flyoutId: this.flyoutId,
          newStatus: false,
          isInnerFlyout: true,
          isBackInteraction: !isClose,
        });
      } else {
        this.$emit('closeFlyout', isClose);
      }

      this.setOpenFlyoutId(null);
    },
    handleResize(event) {
      const currentMq = event.detail.current;
      const isDesktop = desktopBreakpoints.includes(currentMq);

      if (isDesktop && flyoutChainSize === 1) {
        this.closeHeaderFlyout(true);
      } else {
        this.setFlyoutLevels(currentMq);
      }
    },
    handleFlyoutIsOpen() {
      this.initAccessibilityHelper();

      if (this.accessibilityHelper.shouldManageFocus && this.currentFlyoutLevel === flyoutChainSize) {
        this.accessibilityHelper.focusOnNextElement();
      }

      if (this.isSecondaryVmosFlyout) {
        this.openHeaderFlyout([{ infoToAdd: this.flyoutId, flyoutLevel: 3 }]);
        updateActiveFlyout(this.$el, this.flyoutId, true, false, false);
      }
    },
    setFlyoutLevels(currentMq: string) {
      const isDesktop = desktopBreakpoints.includes(currentMq);

      this.isLastLevelDesktop = isDesktop && this.currentFlyoutLevel === 3;
      this.isFirstLevelDesktop = isDesktop && this.currentFlyoutLevel === 2;

      if (this.isThisFlyoutVisible) {
        if (isDesktop && flyoutChainSize <= 1) {
          unlockWindowScroll();
        } else if (!isDesktop && flyoutChainSize > 0) {
          lockWindowScroll();
        }
      }
    },
    /*
     * Find more information about this method on the Header documentation [Accessibility chapter]
     */
    keyDownHandler(event: KeyboardEvent) {
      const keyboardEvent = event;

      headerFlyoutFocusHandler(keyboardEvent, globalFocusStack, flyoutChainSize, this);
    },
    getInnerFlyoutElementType(elementType) {
      const type = FlyoutInnerItemsType[elementType];

      return type ? type : 'div';
    },
    /**
     * Calculate if flyout desktop headline should be shown based on the amount
     * scrolled. If the headline is already under a third of the top bar, show flyout
     */
    calculateHeadlineOpacity() {
      const navElement = this.$refs.flyoutInnerContent;

      if (navElement) {
        const topBarHeight = this.getElementHeight;

        this.shouldShowDesktopTopTitle = navElement.scrollTop > topBarHeight * scrollHeightTriggerPoint;
      }
    },
    stringifiedJsonData(componentData) {
      return JSON.stringify(Object.assign({}, this.childTopicLevel, componentData));
    },
    shouldAddVmosSlot(listItem, index) {
      return listItem.itemType === 'topic' && index === 0 && this.currentFlyoutLevel === 1;
    },
  },
  computed: {
    shouldShowVmos() {
      return Boolean(this.isSecondaryVmosFlyout) || this.getModelTopicId() === this.flyoutId;
    },
    isFirstLevelFlyout() {
      return this.currentFlyoutLevel === 1 || this.isFirstLevelDesktop;
    },
    childTopicLevel() {
      return {
        currentFlyoutLevel: this.currentFlyoutLevel,
      };
    },
    stringifiedSlotData() {
      return JSON.stringify(this.childTopicLevel);
    },
    getElementHeight() {
      const topBarElement = this.$refs.flyoutTopbar;

      return topBarElement ? topBarElement.getBoundingClientRect().height : 0;
    },
    languageConfigurations() {
      return this.languageConfigs && this.languageConfigs.length > 0;
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';
@import './styles/flyout-common';
@import './styles/header-transitions';

.owc-header-flyout {
  position: fixed; //Fix for safari bottom navigation being on top of absolute position elements

  top: 0;
  left: 0;

  z-index: 2;

  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;

  background-color: var(--wb-grey-90);

  @include breakpoint-from(mq3) {
    position: absolute;

    width: $flyout-tablet-width;
    height: $flyout-tablet-height;

    margin: 0;

    border-radius: $flyout-tablet-border-radius;

    overflow-x: hidden;

    background-color: var(--wb-white);
  }
  @include breakpoint-from(mq5) {
    top: 100%;

    width: $flyout-desktop-width;
    height: $flyout-desktop-height;
  }

  .owc-header-flyout {
    &__logo {
      grid-area: logo-title;
    }
    &__topbar {
      position: relative;

      width: 100%;
      min-height: $flyout-top-bar-height;

      background: var(--wb-black);

      @include breakpoint-from(mq3) {
        border-top-left-radius: $flyout-tablet-border-radius;
        border-top-right-radius: $flyout-tablet-border-radius;

        background-color: var(--wb-white);
      }
    }
    &__topbar-content {
      display: grid;

      align-items: center;
      justify-items: stretch;

      height: 100%;

      padding: $flyout-spacing-small $flyout-spacing;

      @include breakpoint-from(mq3) {
        padding: $flyout-spacing-small $flyout-spacing-extra-small;
      }
    }
    &__topbar-fss-content {
      @include breakpoint-to(mq2) {
        border-top: 1px solid var(--wb-grey-20);
      }
    }
    &__topbar-fssElement {
      @include wb-content-width;
      @include breakpoint-from(mq3) {
        display: none;
      }

      position: relative;

      margin: 0 auto;

      &:first-child {
        padding: 8px 0;
      }
    }
    &__topbar-favourite-and-fss {
      display: flex;
      grid-area: search-wishlist;

      @include breakpoint-from(mq3) {
        display: none;
      }
    }
    &__topbar-models {
      @include wb-type-copy-strong;

      display: flex;
      align-items: center;
      justify-content: flex-start;

      color: var(--wb-white);

      @include breakpoint-from(mq3) {
        padding-left: 18px;

        color: var(--wb-black);
      }
    }
    &__topbar-mobileElement {
      @include breakpoint-from(mq3) {
        display: none;
      }
    }
    &__topbar-backButtonWrapper {
      @extend %button-normalize;
      @extend %flyout-button-color;

      display: flex;
      align-items: center;
      justify-content: center;

      width: $button-clickable-area;
      height: $button-clickable-area;
    }
    &__topbar-closeButton {
      grid-area: closeButton;
      justify-self: end;

      @include breakpoint-from(mq3) {
        color: var(--wb-grey-20);
      }
    }
    &__topbarIcon {
      width: $button-inner-icon-size--large;
      height: $button-inner-icon-size--large;
    }
    &__topbar-backButton {
      @include wb-type-copy-strong;

      @extend %button-normalize;
      @extend %flyout-button-color;

      display: flex;
      align-items: center;
      justify-self: flex-start;

      text-align: left;
    }
    &__innerContent {
      flex: 1;

      overflow: auto;

      @include breakpoint-from(mq3) {
        border-bottom-right-radius: $flyout-tablet-border-radius;
        border-bottom-left-radius: $flyout-tablet-border-radius;

        background-color: var(--wb-white);
      }
      @include breakpoint-from(mq5) {
        padding-bottom: $flyout-spacing;
      }
    }
    &__inner-content-title {
      @include wb-type-heading-m;

      display: none;

      margin-bottom: $flyout-spacing;
      padding: 0 $flyout-spacing-major;

      @include breakpoint-from(mq5) {
        display: block;
      }
    }
    &__navigation-list {
      box-shadow: $flyout-mobile-box-shadow;

      @include breakpoint-to(mq2) {
        padding: $flyout-spacing 0;
      }
      @include breakpoint-between(mq3, mq5) {
        border-bottom: 1px solid var(--wb-grey-85);
      }

      slot,
      & > .webcomponent.webcomponent-simulator {
        display: flex;
        flex-direction: column;
      }

      @include breakpoint-from(mq3) {
        box-shadow: none;
      }
      @include breakpoint-from(mq5) {
        padding: 0 $flyout-spacing-major $flyout-spacing-extra-small $flyout-spacing-major;
      }
    }
    &__inner-content-list {
      padding: 0 $flyout-spacing;
    }
    &__bottom-bar {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 100%;
      height: $flyout-bottom-bar-height;

      background-color: var(--wb-black);

      @include breakpoint-from(mq3) {
        display: none;
      }
    }
    &__imprint {
      @include wb-type-hint;
      @include wb-content-width;

      display: block;

      padding: $flyout-spacing 0;

      color: var(--wb-grey-20);

      text-decoration: none;

      @include breakpoint-from(mq5) {
        display: none;
      }
    }
  }
  /*
    * Grid areas that represent hardcoded elements are not
    * attached to actual elements since that's not necessary
    */
  &.owc-header-flyout__first-level {
    .owc-header-flyout__topbar-content {
      grid-column-gap: 0;
      grid-template-areas: 'search-wishlist logo-title closeButton';
      grid-template-columns: $button-clickable-area * 2 auto $button-clickable-area * 2;

      @include breakpoint-from(mq5) {
        grid-template-areas: 'wishlist logo-title closeButton';
        grid-template-columns: $button-clickable-area auto $button-clickable-area * 2;
      }
    }
    .owc-header-flyout__navigation-list {
      &--first-level > .owc-header-navigation-topic {
        &:not(:last-child) {
          @include breakpoint-to(mq4) {
            border-bottom: 1px solid var(--wb-grey-85);
          }
        }
      }
    }
    .owc-header-flyout__inner-content-list {
      @include breakpoint-from(mq5) {
        padding: 0 $flyout-spacing-major;
      }
    }

    @include breakpoint-from(mq3) {
      margin-top: $flyout-spacing;
    }
    @include breakpoint-from(mq5) {
      margin-top: $flyout-spacing-small;

      overflow: visible;
    }
  }
  &.owc-header-flyout__last-level {
    top: 0;
    left: $flyout-desktop-width + 16px;

    width: $flyout-desktop-width-level-3;

    .owc-header-flyout__navigation-list {
      @include breakpoint-from(mq5) {
        padding: 0 $flyout-spacing;
      }
    }
  }
  &:not(.owc-header-flyout__first-level) {
    z-index: 2;

    .owc-header-flyout__topbar-content {
      grid-column-gap: 0;
      grid-template-areas: 'backArrowAndtitle closeButton';
      grid-template-columns: auto $button-clickable-area;

      justify-content: space-between;
    }
  }
  &__topbar-desktop-title {
    @include wb-type-heading-xs;

    display: none;
    grid-area: logo-title;
    align-self: center;

    padding-left: $flyout-spacing;

    opacity: 0;

    transition: 100ms opacity ease-in-out;

    &:before {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;

      border-bottom: 1px solid var(--wb-grey-90);

      content: '';
    }
    &--show {
      opacity: 1;
    }

    @include breakpoint-from(mq5) {
      display: block;
    }
  }
}

@include rtl-rule('.owc-header-flyout', false) {
  right: 0;
  left: unset;

  &__last-level {
    right: $flyout-desktop-width + 16px;
    left: unset;
  }
}
@include rtl-rule('.owc-header-flyout') {
  &__last-level {
    right: $flyout-desktop-width + 16px;
    left: unset;
  }
}
</style>
